import { gatewayClient, createService } from './connectors/httpClient';
import { gatewayStream } from './connectors/sseClient';
import { Asset, AssetStatus } from 'packages/types/assets';
import { type UUID } from 'packages/types/uuid';

const fieldsMap = new Map([
    ['name', 'FILENAME'],
    ['size', 'FILESIZE'],
    ['date', 'CREATED'],
    ['date_created', 'CREATED'],
    ['UPDATED', 'UPDATED'],
    ['date_updated', 'UPDATED'],
    ['type', 'EXTENSION'],
    ['resolution', 'RESOLUTION'],
    ['duration', 'DURATION'],
]);

const httpClient = gatewayClient('/assets');
const statusesStream = gatewayStream('/assets');

const assetsService = createService(httpClient, (client) => ({
    getAssets({ sort, page, filter, search, size, customerId, thumbnails, type, collectionId, includeAssetId }) {
        const types = Array.isArray(type) ? type : [type];

        return client
            .get(
                `?customerId=${customerId}&` +
                    `thumbnails=${thumbnails}&` +
                    `size=${size}&` +
                    `sortingField=${fieldsMap.get(sort[0])}&` +
                    `sortingOrder=${sort[1].toUpperCase()}${filter !== 'all' ? '&status=' + filter : ''}&` +
                    `page=${page}` +
                    `${search ? '&query=' + search : ''}` +
                    `${type ? types.reduce((accum, current) => accum + '&type=' + current, '') : ''}` +
                    `${collectionId ? '&collectionId=' + collectionId : ''}` +
                    `${includeAssetId ? '&includeAssetId=' + includeAssetId : ''}` +
                    '&includeCollectionIds=true',
            )
            .then(({ json, status }) => {
                return {
                    assets: json ? (json as any).items : null,
                    status,
                    totalCount: json ? (json as any).totalItems : null,
                    page: (json as any).page,
                };
            });
    },

    getAsset(id, thumbnails = null) {
        const path = `/${id}?` + `${thumbnails ? 'thumbnails=' + thumbnails : ''}` + '&includeCollectionIds=true';

        return client.get<Asset>(path).then(({ json, status }) => {
            return { asset: json, status };
        });
    },

    getUploadOptions(customerId) {
        return client.get(`/upload-options/${customerId}`).then(({ json, status }) => {
            return { json, status };
        });
    },

    saveAssets(opt, queryParams) {
        const { thumbnails } = queryParams;
        let path = '';

        if (thumbnails) {
            path += `?thumbnails=${thumbnails}`;
        }

        return client.post(path, opt).then(({ json, status }) => {
            return { path: json, status };
        });
    },

    deleteAssets(opt) {
        return client.delete('', opt).then(({ json, status }) => {
            return { path: json, status };
        });
    },

    editFileName(id, fileName) {
        return client.put(`/${id}`, { fileName }).then(({ json, status }) => {
            return { asset: json, status };
        });
    },

    createAssets(customerId, items) {
        return client.post('/store-path', { customerId, items }).then(({ json }) => (json as any)?.items || []);
    },

    updateAssetStatus(data) {
        return client.post('/update-status', data);
    },

    prepareAIEdit(assetId: string): Promise<{
        aiEditData: { assetId: string; transformationId: number | null; fileId: string };
        status: number;
    }> {
        return client
            .post<{
                assetId: string;
                transformationId: number | null;
                fileId: string;
            }>(`/${assetId}/prepare-ai-edit`, {})
            .then(({ json, status }) => {
                return { aiEditData: json, status };
            });
    },

    saveAIEdit({
        fileId,
        name,
        copyMetaproperties,
    }: {
        fileId: string;
        name: string;
        copyMetaproperties?: boolean;
    }): Promise<{ asset: any; status: number }> {
        return client.post(`/files/${fileId}`, { name, copyMetaproperties }).then(({ json, status }) => {
            return { asset: json, status };
        });
    },
}));

export type AssetStatusItem = {
    id: UUID;
    status: AssetStatus;
    progress?: number;
    thumbnails?: { [key: string]: string };
};

// Closing from inside onMessage is the caller's job: useSseSubscribe hands its own unsubscribe to
// the consumer, so the stream handle stays private to the hook.
export function listenToAssetStatuses(
    assetIds: string[],
    onItems: (items: AssetStatusItem[]) => void,
    onClose?: () => void,
): () => void {
    // The body key is `ids`, per AssetIdsRequestDto, not `assetIds` as the STOMP publish body used.
    // A wrong key deserialises to null and @NotEmpty rejects it with a bodyless 400.
    return statusesStream.subscribe<{ totalItems: number; items: AssetStatusItem[] }, { ids: string[] }>(
        '/statuses/stream',
        {
            eventName: 'status',
            body: { ids: assetIds },
            onMessage: ({ items }) => onItems(items),
            onClose,
        },
    );
}

export default assetsService;
